import React, { useState } from "react";
import { format } from "date-fns";
import AmountInput from "../../../../components/JewelleryPlans/common/AmountInput";
import { DocumentSearchIcon } from "@heroicons/react/outline";

const VerificationModal = ({ payment, onClose, onVerify }) => {
  const [verifiedAmount, setVerifiedAmount] = useState(payment.amountCollected);
  const [note, setNote] = useState("");
  const [showProof, setShowProof] = useState(false);
  const [varianceApproval, setVarianceApproval] = useState({
    required: false,
    approvedBy: "",
    approvalNote: "",
  });

  const variance =
    ((verifiedAmount - payment.amountCollected) / payment.amountCollected) *
    100;
  const requiresApproval = Math.abs(variance) > 10;

  const handleSubmit = () => {
    const data = {
      verifiedAmount,
      note,
      ...(requiresApproval && {
        varianceApprovedBy: varianceApproval.approvedBy,
        varianceApprovalNote: varianceApproval.approvalNote,
      }),
    };
    onVerify(data);
  };

  return (
    <div className="fixed inset-0 bg-neutral-dark bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-background rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-light">
          <h3 className="text-lg font-medium text-text">Verify Payment</h3>
          <p className="mt-1 text-sm text-neutral-dark">
            Review and verify payment details
          </p>
        </div>

        {/* Content */}
        <div className="px-6 py-4 space-y-4">
          {/* Payment Details */}
          <div className="bg-neutral-light rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-neutral-dark">Plan Number</p>
                <p className="font-medium">{payment.planId?.planNumber}</p>
              </div>
              <div>
                <p className="text-neutral-dark">Customer</p>
                <p className="font-medium">{payment.customerId?.name}</p>
              </div>
              <div>
                <p className="text-neutral-dark">Collection Date</p>
                <p className="font-medium">
                  {format(new Date(payment.dateCollected), "dd MMM yyyy")}
                </p>
              </div>
              <div>
                <p className="text-neutral-dark">Payment Method</p>
                <p className="font-medium">{payment.method}</p>
              </div>
              <div>
                <p className="text-neutral-dark">Amount Collected</p>
                <p className="font-medium">
                  ₹{payment.amountCollected?.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-neutral-dark">Entered By</p>
                <p className="font-medium">{payment.enteredBy?.name}</p>
              </div>
              {payment.externalReference && (
                <>
                  <div>
                    <p className="text-neutral-dark">Reference</p>
                    <p className="font-medium font-mono text-xs">
                      {payment.externalReference}
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Payment Proof */}
          {payment.proofUrl && (
            <div>
              <button
                onClick={() => setShowProof(!showProof)}
                className="inline-flex items-center px-3 py-2 border border-neutral-light rounded-md text-sm font-medium text-text bg-background hover:bg-neutral-light"
              >
                <DocumentSearchIcon className="h-4 w-4 mr-2" />
                {showProof ? "Hide" : "View"} Payment Proof
              </button>
              {showProof && (
                <div className="mt-2 border border-neutral-light rounded-lg p-2">
                  <img
                    src={payment.proofUrl}
                    alt="Payment proof"
                    className="max-w-full h-auto"
                  />
                </div>
              )}
            </div>
          )}

          {/* Verification Amount */}
          <AmountInput
            label="Verified Amount"
            value={verifiedAmount}
            onChange={setVerifiedAmount}
            required
            showVariance={true}
            expectedAmount={payment.amountCollected}
          />

          {/* High Variance Warning */}
          {requiresApproval && (
            <div className="bg-warning-light border border-warning rounded-lg p-4">
              <p className="text-sm font-medium text-warning-dark mb-3">
                ⚠️ High variance detected ({variance.toFixed(1)}%)
              </p>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-text">
                    Approved By (Manager/Owner) *
                  </label>
                  <input
                    type="text"
                    value={varianceApproval.approvedBy}
                    onChange={(e) =>
                      setVarianceApproval({
                        ...varianceApproval,
                        approvedBy: e.target.value,
                      })
                    }
                    className="mt-1 block w-full border-neutral-light rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                    placeholder="Enter approver name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text">
                    Approval Note
                  </label>
                  <textarea
                    value={varianceApproval.approvalNote}
                    onChange={(e) =>
                      setVarianceApproval({
                        ...varianceApproval,
                        approvalNote: e.target.value,
                      })
                    }
                    rows={2}
                    className="mt-1 block w-full border-neutral-light rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                    placeholder="Reason for variance..."
                  />
                </div>
              </div>
            </div>
          )}

          {/* Verification Note */}
          <div>
            <label className="block text-sm font-medium text-text">
              Verification Note (Optional)
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              className="mt-1 block w-full border-neutral-light rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
              placeholder="Any additional notes..."
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-neutral-light flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-neutral-light rounded-md text-sm font-medium text-text bg-background hover:bg-neutral-light"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={requiresApproval && !varianceApproval.approvedBy}
            className={`px-4 py-2 rounded-md text-sm font-medium text-text-on-primary
              ${
                requiresApproval && !varianceApproval.approvedBy
                  ? "bg-neutral-dark cursor-not-allowed"
                  : "bg-success hover:bg-success-dark"
              }`}
          >
            Verify Payment
          </button>
        </div>
      </div>
    </div>
  );
};

export default VerificationModal;
